import { formatDistanceToNow } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

import { DATE_FORMATS, DEFAULT_TIMEZONE } from '../constants';
import { type DateInput, TimeFormat, type TimezoneString } from '../types';
import { formatWithPattern, toDate } from './custom';

/**
 * Formats a date into a display-friendly string (e.g., 'Jan 15, 2024').
 *
 * @param date - The date to format.
 * @param timezone - The target timezone (defaults to 'UTC').
 * @returns A formatted date string using the 'MMM dd, yyyy' pattern.
 */
export function formatDate(date: DateInput, timezone: TimezoneString = DEFAULT_TIMEZONE): string {
  const d = toDate(date);
  return formatInTimeZone(d, timezone, DATE_FORMATS.DISPLAY);
}

/**
 * Formats a date and time into a display string (e.g., 'Jan 15, 2024 3:45 PM').
 *
 * @param date - The date to format.
 * @param timezone - The target timezone (defaults to 'UTC').
 * @returns A formatted date-time string using the 'MMM dd, yyyy h:mm a' pattern.
 */
export function formatDateTime(date: DateInput, timezone: TimezoneString = DEFAULT_TIMEZONE): string {
  const d = toDate(date);
  return formatInTimeZone(d, timezone, DATE_FORMATS.DATETIME_DISPLAY);
}

/**
 * Formats a date into a localized time-only string (e.g., '3:45 PM').
 *
 * @param date - The date to format.
 * @param timezone - The target timezone (defaults to 'UTC').
 * @returns A time string using the 'h:mm a' pattern.
 */
export function formatTime(date: DateInput, timezone: TimezoneString = DEFAULT_TIMEZONE): string {
  const d = toDate(date);
  return formatInTimeZone(d, timezone, DATE_FORMATS.TIME_DISPLAY);
}

/**
 * Formats a date into an ISO 8601 time string (e.g., '2024-01-15T15:45:00Z').
 *
 * @param date - The date to format.
 * @param timezone - The target timezone (defaults to 'UTC').
 * @returns A string formatted in ISO 8601 UTC style.
 */
export function formatTimeISO(date: DateInput, timezone: TimezoneString = DEFAULT_TIMEZONE): string {
  const d = toDate(date);
  return formatInTimeZone(d, timezone, DATE_FORMATS.ISO);
}

/**
 * Formats a date relative to the current time (e.g., 'about 1 hour ago', 'less than a minute ago').
 *
 * @param date - The date to format.
 * @returns A human-readable relative time string with suffix.
 */
export function formatRelative(date: DateInput): string {
  return formatDistanceToNow(toDate(date), { addSuffix: true });
}

/**
 * Formats a date into a readable string.
 * @param dateInput - The date to be formatted, which can be either an ISO date string or a Date object.
 * @param timezone
 * @returns The formatted date string in 'MMM dd, yyyy' format (e.g., 'Aug 09, 2024').
 * Example usage:
 *   formatDateForDisplay("2024-08-09T00:00:00.000Z"); // Returns 'Aug 09, 2024'
 */
export function formatDateForDisplay(dateInput: DateInput, timezone: TimezoneString = DEFAULT_TIMEZONE): string {
  return formatDate(dateInput, timezone);
}

/**
 * Formats a date and time into a readable string in a given timezone.
 * @param dateInput - The date to be formatted, which can be either an ISO date string or a Date object.
 * @param timezone - The IANA timezone string (e.g., 'America/Chicago').
 * @param timeFormat - The desired time format, either '24hr' or 'ampm'.
 * @returns The formatted time string in the appropriate format (e.g., '3:00 PM' or '15:00')
 * Example usage:
 *   formatTimeForDisplay("2024-08-09T15:00:00.000Z", 'America/Chicago', 'ampm'); // Returns '10:00 AM' if in Central Daylight Time
 */
export function formatTimeForDisplay(dateInput: DateInput, timezone: TimezoneString, timeFormat: TimeFormat): string {
  return timeFormat === TimeFormat['24hr']
    ? formatWithPattern(dateInput, DATE_FORMATS.TIME_24H, timezone)
    : formatTime(dateInput, timezone);
}
